<script setup lang="ts">
import PositionChips from '@/components/finest-custom-position-list/PositionChips.vue'
import PositionColumnTable from '@/components/finest-custom-position-list/PositionColumnTable.vue'
import PositionValueTable from '@/components/finest-custom-position-list/PositionValueTable.vue'
import { unusedPositions, visiblePositions } from '@/components/finest-custom-position-list/positions'
import type {
  PositionCellValue,
  PositionColumn,
  PositionRow,
  PositionTotalRow,
  PositionValue,
  PredefinedPosition,
} from '@/components/finest-custom-position-list/types'
import { computed, useTemplateRef } from 'vue'

const props = withDefaults(
  defineProps<{
    title: string
    positionHeader?: string
    valueHeader?: string
    keys?: PredefinedPosition[]
    columns?: PositionColumn[]
    editableRows?: boolean
    addLabel?: string
    emptyLabel?: string
    valueAddonLabel?: string
    minFractionDigits?: number
    maxFractionDigits?: number
    min?: number
    max?: number
    showTotal?: boolean
    totalRows?: PositionTotalRow[]
    plain?: boolean
    flush?: boolean
  }>(),
  {
    positionHeader: '',
    valueHeader: '',
    keys: () => [],
    columns: undefined,
    editableRows: false,
    addLabel: undefined,
    emptyLabel: undefined,
    valueAddonLabel: undefined,
    minFractionDigits: 2,
    maxFractionDigits: 2,
    min: undefined,
    max: undefined,
    showTotal: true,
    totalRows: undefined,
    plain: false,
    flush: false,
  },
)

const model = defineModel<PositionValue[]>({ default: () => [] })
const rows = defineModel<PositionRow[]>('rows', { default: () => [] })
const totalInputs = defineModel<Record<string, PositionCellValue>>('totalInputs', { default: () => ({}) })

const emit = defineEmits<{
  change: [values: PositionValue[]]
  addRow: []
  removeRow: [key: string]
  commit: []
  commitTotal: [key: string]
}>()

const isColumnMode = computed(() => (props.columns?.length ?? 0) > 0)

const positions = computed(() => visiblePositions(props.keys, model.value ?? []))

const availablePositions = computed(() =>
  unusedPositions(props.keys, new Set(positions.value.map((position) => position.key))),
)

const hasTitle = computed(() => props.title.trim().length > 0)

const valueTable = useTemplateRef<{ addPosition: (position: PredefinedPosition) => void }>('valueTable')

const cardClass = computed(() => {
  if (props.plain) {
    return ''
  }

  return props.flush ? 'fh-card py-6' : 'fh-card p-6'
})

/** With a flush table the card keeps no horizontal padding, so everything above it carries its own. */
const insetClass = computed(() => (props.flush && !props.plain ? 'px-6' : ''))
</script>

<template>
  <div :class="cardClass">
    <h2 v-if="hasTitle" class="mb-4" :class="insetClass">{{ title }}</h2>

    <PositionChips
      v-if="!isColumnMode && availablePositions.length"
      :class="insetClass"
      :positions="availablePositions"
      @pick="valueTable?.addPosition($event)" />

    <div v-if="$slots.actions" class="mb-8" :class="insetClass">
      <slot name="actions" />
    </div>

    <div class="overflow-x-auto">
      <PositionColumnTable
        v-if="isColumnMode"
        v-model="rows"
        v-model:total-inputs="totalInputs"
        :columns="columns ?? []"
        :editable-rows="editableRows"
        :show-total="showTotal"
        :total-rows="totalRows"
        :max-fraction-digits="maxFractionDigits"
        :add-label="addLabel"
        :empty-label="emptyLabel"
        :inset="flush"
        @add-row="emit('addRow')"
        @remove-row="emit('removeRow', $event)"
        @commit="emit('commit')"
        @commit-total="emit('commitTotal', $event)" />

      <PositionValueTable
        v-else
        ref="valueTable"
        v-model="model"
        :keys="keys"
        :position-header="positionHeader"
        :value-header="valueHeader"
        :show-total="showTotal"
        :unit="valueAddonLabel ?? '€'"
        :min-fraction-digits="minFractionDigits"
        :max-fraction-digits="maxFractionDigits"
        :min="min"
        :max="max"
        @change="emit('change', $event)" />
    </div>
  </div>
</template>
